import {
  useLayoutEffect,
  useState,
  useEffect,
  useRef,
  useMemo,
  memo,
  type ReactNode,
} from "react";
import { Handle, Position, NodeToolbar } from "@xyflow/react";
import type { Node, NodeProps } from "@xyflow/react";
import { useStore } from "../App/store";
import { shallow } from "zustand/shallow";
import {
  sanitizeLabel,
  toggleBold,
  applyTextColor,
  removeTextColor,
  saveCursorPosition,
  restoreCursorPosition,
  FLAG_COLORS,
} from "../utils/textFormatting";
import { CiSquarePlus, CiSquareMinus } from "react-icons/ci";
import { BsFillInfoCircleFill } from "react-icons/bs";
import { IoFlag } from "react-icons/io5";
import { LuCheck } from "react-icons/lu";
import { MdOutlineAddTask, MdPerson, MdLocationOn, MdWarning } from "react-icons/md";
import { FaHandshake } from "react-icons/fa";
import { NodeData } from "../types/types";
import { OverlayPanel } from "primereact/overlaypanel";
import { Tag } from "primereact/tag";
import ReactMarkdown from "react-markdown";
import { convertHtmlToMarkdown } from "../Helpers/NodeHelpers";
import WhyDialog from "./WhyDialog";
import { getCalculatedValue } from "../Helpers/Calculations";

function FocusItem({
  id,
  data,
  isConnectable,
  selected,
}: NodeProps<Node<NodeData>>) {
  const inputRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const noteDetailsRef = useRef<HTMLDivElement>(null);
  const op = useRef<OverlayPanel>(null);

  // Combined selector for modal-related state
  const {
    setSelectedNodeId,
    setSelectedNode,
    selectedNodeId,
    setShowNotesModal,
    setCurrentNotes,
    setCurrentLinks,
    setCurrentNodeLabel,
    noteDetailsNodeId,
    setNoteDetailsNodeId,
  } = useStore(
    (state) => ({
      setSelectedNodeId: state.setSelectedNodeId,
      setSelectedNode: state.setSelectedNode,
      selectedNodeId: state.selectedNodeId,
      setShowNotesModal: state.setShowNotesModal,
      setCurrentNotes: state.setCurrentNotes,
      setCurrentLinks: state.setCurrentLinks,
      setCurrentNodeLabel: state.setCurrentNodeLabel,
      noteDetailsNodeId: state.noteDetailsNodeId,
      setNoteDetailsNodeId: state.setNoteDetailsNodeId,
    }),
    shallow,
  );

  const isNodeSelected = selectedNodeId === id;
  const presentationOrderInputRef = useRef<HTMLInputElement>(null);
  const priorityInputRef = useRef<HTMLInputElement>(null);
  const valueInputRef = useRef<HTMLInputElement>(null);
  const showDetails = noteDetailsNodeId === id;
  const [showWhyDialog, setShowWhyDialog] = useState(false);

  // Combined selector for user and node data
  const { user, getNode, edges, updateNodeData } = useStore(
    (state) => ({
      user: state.user,
      getNode: state.getNode,
      edges: state.edges,
      updateNodeData: state.updateNodeData,
    }),
    shallow,
  );

  const showWhyTag = user?.settings?.showWhyTag !== false; // Default to true

  // Detect if a URL points to a Word document
  const isWordDocumentUrl = (url: string): boolean => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return (
      lower.endsWith(".docx") ||
      lower.endsWith(".doc") ||
      lower.includes(".docx?") ||
      lower.includes(".doc?") ||
      (lower.includes("sharepoint.com") && lower.includes(".docx")) ||
      lower.includes("office.live.com") ||
      lower.includes("1drv.ms")
    );
  };

  // Derive the node type — explicit type wins; otherwise infer from the first link
  const derivedType = useMemo(() => {
    if (data.type) return data.type as string;
    const firstUrl = data.links?.[0]?.url || "";
    if (isWordDocumentUrl(firstUrl)) return "word";
    return null;
  }, [data.type, data.links]);

  // Build arc satellite badges — same logic as NoteItem
  const satelliteBadges: { key: string; content: ReactNode }[] = useMemo(() => {
    const badges: { key: string; content: ReactNode }[] = [];

    const linkUrl = data.links?.[0]?.url || "";
    if (linkUrl) {
      let linkContent: ReactNode;
      if (isWordDocumentUrl(linkUrl)) {
        linkContent = <i className="pi pi-file-word" style={{ fontSize: 14, color: "#2b579a" }} />;
      } else {
        try {
          const domain = new URL(linkUrl).hostname;
          linkContent = (
            <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
          );
        } catch {
          linkContent = <i className="pi pi-link" style={{ fontSize: 14 }} />;
        }
      }
      badges.push({ key: "link", content: linkContent });
    }

    if (data.icon && data.icon.length > 0) {
      let imgContent: ReactNode;
      if (/^https?:\/\//.test(data.icon) || data.icon.startsWith("data:image/") || data.icon.endsWith(".png") || data.icon.endsWith(".jpg") || data.icon.endsWith(".jpeg")) {
        imgContent = <img src={data.icon} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />;
      } else {
        imgContent = <span style={{ fontSize: 15, lineHeight: 1 }}>{data.icon}</span>;
      }
      badges.push({ key: "image", content: imgContent });
    }

    if (derivedType) {
      const typeIconMap: Record<string, ReactNode> = {
        task: <MdOutlineAddTask size={16} />,
        resource: <MdPerson size={16} />,
        location: <MdLocationOn size={16} />,
        risk: <MdWarning size={16} />,
        decision: <FaHandshake size={15} />,
        word: <i className="pi pi-file-word" style={{ fontSize: 14, color: "#2b579a" }} />,
      };
      badges.push({ key: "type", content: typeIconMap[derivedType] ?? <span style={{ fontSize: 9, fontWeight: 700 }}>{derivedType}</span> });
    }

    return badges;
  }, [data.links, data.icon, derivedType]);

  const ARC_RADIUS_FOCUS = 38; // larger radius for the FocusItem ring
  const BADGE_SIZE_FOCUS = 20;
  const SPREAD_DEG_FOCUS = 100;

  const renderFocusSatelliteBadges = () => {
    const total = satelliteBadges.length;
    if (total === 0) return null;

    return satelliteBadges.map(({ key, content }, i) => {
      // 1 badge → straight north; multiple → evenly spread over the arc
      let angleDeg: number;
      if (total === 1) {
        angleDeg = -90;
      } else {
        const startDeg = -90 - SPREAD_DEG_FOCUS / 2;
        const step = SPREAD_DEG_FOCUS / (total - 1);
        angleDeg = startDeg + i * step;
      }
      const angleRad = (angleDeg * Math.PI) / 180;
      const x = ARC_RADIUS_FOCUS * Math.cos(angleRad);
      const y = ARC_RADIUS_FOCUS * Math.sin(angleRad);
      return (
        <div
          key={key}
          className="focus-satellite-badge"
          style={{
            left: `calc(50% + ${x}px - ${BADGE_SIZE_FOCUS / 2}px)`,
            top: `calc(50% + ${y}px - ${BADGE_SIZE_FOCUS / 2}px)`,
          }}
        >
          {content}
        </div>
      );
    });
  };

  // Check if this is a root node (no incoming edges)
  const isRootNode = !edges.some((edge) => edge.target === id);

  const activeDrilldownTab = useStore((state) => state.activeDrilldownTab);
  const drilldownTabs = useStore((state) => state.drilldownTabs);

  // Check if this node is the focus of an active drill-down tab
  const isDrilldownFocus =
    activeDrilldownTab &&
    drilldownTabs.some(
      (tab) => tab.id === activeDrilldownTab && tab.nodeId === id,
    );

  useLayoutEffect(() => {
    if (inputRef.current && wrapperRef.current) {
      wrapperRef.current.style.width = inputRef.current.style.width;
      wrapperRef.current.style.height = inputRef.current.style.height;
    }
  }, [inputRef.current?.style.width, inputRef.current?.style.height]);

  // Combined selector for update functions and presentation state
  const {
    updateNodeLabel,
    updatePresentationOrder,
    updatePriority,
    updateValue,
    focusValue,
    isPresentationMode,
    presentWithShowHideButtons,
  } = useStore(
    (state) => ({
      updateNodeLabel: state.updateNodeLabel,
      updatePresentationOrder: state.updatePresentationOrder,
      updatePriority: state.updatePriority,
      updateValue: state.updateValue,
      focusValue: state.focusValue,
      isPresentationMode: state.isPresentationMode,
      presentWithShowHideButtons: state.presentWithShowHideButtons,
    }),
    shallow,
  );

  // Get presentationOrder directly from store for reactivity
  const presentationOrder = useStore((state) => {
    const node = state.nodeMap.get(id);
    return node?.data.presentationOrder || "";
  });

  const cursorPositionRef = useRef<number | null>(null);
  const isComposingRef = useRef<boolean>(false);
  const isFocusedRef = useRef<boolean>(false);
  const skipCursorRestore = useRef<boolean>(false);

  const updateFocusHandleSize = (
    contentWidth: number,
    contentHeight: number,
  ) => {
    if (!dragRef.current) return;

    const minDiameter = 56;
    const ringPadding = 20;
    const diameter = Math.max(minDiameter, contentWidth + ringPadding, contentHeight + ringPadding);

    dragRef.current.style.width = `${diameter}px`;
    dragRef.current.style.height = `${diameter}px`;
  };

  const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    isFocusedRef.current = true;
    setSelectedNodeId(id);
    // Select all text in contenteditable
    const range = document.createRange();
    const selection = window.getSelection();
    range.selectNodeContents(event.target);
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    setSelectedNodeId(null);
  };

  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus({ preventScroll: true });
    }, 1);
  }, []);

  // Use useEffect instead of useLayoutEffect to avoid blocking LCP paint
  useEffect(() => {
    if (inputRef.current) {
      const minWidth = 50;
      const padding = 10;

      inputRef.current.setAttribute("spellcheck", "false");
      // Only update innerHTML if not currently focused (to avoid cursor jumping during typing)
      if (!isFocusedRef.current && inputRef.current.innerHTML !== data.label) {
        inputRef.current.innerHTML = data.label;
      }
      // Reset height to auto to get the correct scrollHeight
      inputRef.current.style.height = "auto";

      // Set height based on content
      const scrollHeight = inputRef.current.scrollHeight;
      inputRef.current.style.height = `${scrollHeight}px`;

      // For multi-line content, measure the width of the longest line
      const textContent = inputRef.current.textContent || "";
      const lines = textContent.split("\n");

      let maxLineWidth = minWidth;

      if (lines.length > 1) {
        // Multi-line: measure each line
        const tempSpan = document.createElement("span");
        tempSpan.style.visibility = "hidden";
        tempSpan.style.position = "absolute";
        tempSpan.style.whiteSpace = "nowrap";
        tempSpan.style.font = window.getComputedStyle(inputRef.current).font;
        document.body.appendChild(tempSpan);

        lines.forEach((line) => {
          tempSpan.textContent = line;
          const lineWidth = tempSpan.offsetWidth;
          if (lineWidth > maxLineWidth) {
            maxLineWidth = lineWidth;
          }
        });

        document.body.removeChild(tempSpan);
      } else {
        // Single line: use scrollWidth
        const originalWhiteSpace = inputRef.current.style.whiteSpace;
        inputRef.current.style.width = "auto";
        inputRef.current.style.whiteSpace = "nowrap";
        inputRef.current.offsetWidth; // trigger reflow
        maxLineWidth = inputRef.current.scrollWidth;
        inputRef.current.style.whiteSpace = originalWhiteSpace;
      }

      const calculatedWidth = Math.max(minWidth, maxLineWidth + padding);
      inputRef.current.style.width = `${calculatedWidth}px`;

      updateFocusHandleSize(calculatedWidth, scrollHeight);
    }
  }, [data.label]);

  const handleChange = (evt: React.FormEvent<HTMLDivElement>) => {
    // Save cursor position before update
    const cursorPos = saveCursorPosition(evt.currentTarget);
    const htmlContent = evt.currentTarget.innerHTML;

    // Adjust dimensions based on content
    const element = evt.currentTarget;
    const minWidth = 50;
    const padding = 10;

    // For multi-line content, measure the width of the longest line
    const textContent = element.textContent || "";
    const lines = textContent.split("\n");

    let maxLineWidth = minWidth;

    if (lines.length > 1) {
      // Multi-line: measure each line
      const tempSpan = document.createElement("span");
      tempSpan.style.visibility = "hidden";
      tempSpan.style.position = "absolute";
      tempSpan.style.whiteSpace = "nowrap";
      tempSpan.style.font = window.getComputedStyle(element).font;
      document.body.appendChild(tempSpan);

      lines.forEach((line) => {
        tempSpan.textContent = line;
        const lineWidth = tempSpan.offsetWidth;
        if (lineWidth > maxLineWidth) {
          maxLineWidth = lineWidth;
        }
      });

      document.body.removeChild(tempSpan);
    } else {
      // Single line: use scrollWidth
      const originalWhiteSpace = element.style.whiteSpace;
      element.style.width = "auto";
      element.style.whiteSpace = "nowrap";
      maxLineWidth = element.scrollWidth;
      element.style.whiteSpace = originalWhiteSpace;
    }

    // Adjust height
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;

    // Set width based on longest line
    const calculatedWidth = Math.max(minWidth, maxLineWidth + padding);
    element.style.width = `${calculatedWidth}px`;

    updateFocusHandleSize(calculatedWidth, element.scrollHeight);

    updateNodeLabel(id, htmlContent);

    // Restore cursor immediately (not in requestAnimationFrame) since we're preventing React from updating innerHTML
    // Skip restoration for natural editing operations like Enter key
    if (cursorPos && !skipCursorRestore.current) {
      restoreCursorPosition(element, cursorPos);
    }

    // Reset the flag
    skipCursorRestore.current = false;
  };

  const handleCompositionStart = () => {
    isComposingRef.current = true;
  };

  const handleCompositionEnd = (
    evt: React.CompositionEvent<HTMLDivElement>,
  ) => {
    isComposingRef.current = false;
  };

  const handleKeyDown = (evt: React.KeyboardEvent<HTMLDivElement>) => {
    // Handle Enter key - insert <br/> tag instead of creating new block elements
    if (evt.key === "Enter" && !evt.shiftKey && !evt.ctrlKey && !evt.metaKey) {
      evt.preventDefault();
      // Insert a <br/> tag at cursor position
      document.execCommand("insertLineBreak");
      return;
    }

    // Handle text formatting shortcuts
    if (evt.ctrlKey || evt.metaKey) {
      if (evt.key === "b" || evt.key === "B") {
        evt.preventDefault();
        toggleBold();
        // Manually save the label after formatting
        if (inputRef.current) {
          const htmlContent = inputRef.current.innerHTML;
          updateNodeLabel(id, htmlContent);
        }
        return;
      }
      if (evt.key === "1") {
        evt.preventDefault();
        applyTextColor(FLAG_COLORS.green);
        // Manually save the label after applying color
        if (inputRef.current) {
          const htmlContent = inputRef.current.innerHTML;
          updateNodeLabel(id, htmlContent);
        }
        return;
      }
      if (evt.key === "2") {
        evt.preventDefault();
        applyTextColor(FLAG_COLORS.yellow);
        // Manually save the label after applying color
        if (inputRef.current) {
          const htmlContent = inputRef.current.innerHTML;
          updateNodeLabel(id, htmlContent);
        }
        return;
      }
      if (evt.key === "3") {
        evt.preventDefault();
        applyTextColor(FLAG_COLORS.red);
        // Manually save the label after applying color
        if (inputRef.current) {
          const htmlContent = inputRef.current.innerHTML;
          updateNodeLabel(id, htmlContent);
        }
        return;
      }
      if (evt.key === "0") {
        evt.preventDefault();
        removeTextColor();
        // Manually save the label after removing color
        if (inputRef.current) {
          const htmlContent = inputRef.current.innerHTML;
          updateNodeLabel(id, htmlContent);
        }
        return;
      }
    }

    // Shift+Enter also inserts <br/> (same as Enter now)
    if (evt.key === "Enter" && evt.shiftKey) {
      evt.preventDefault();
      document.execCommand("insertLineBreak");
      return;
    }

    // Cmd+Enter (Mac) or Ctrl+Enter to create child nodes
    if (evt.key === "Enter" && (evt.metaKey || evt.ctrlKey)) {
      evt.preventDefault();

      // Get current state from store
      const store = useStore.getState();
      const tabModeActive = store.tabModeActive;
      const tabModeParentId = store.tabModeParentId;

      // Determine the parent node for tab mode
      let parentNodeId = id;

      if (tabModeActive && tabModeParentId) {
        // Continue with existing parent
        parentNodeId = tabModeParentId;
      } else {
        // Start new tab mode with current node as parent
        store.setTabModeActive(true);
        store.setTabModeParentId(id);
      }

      // Create new child node
      const newNodeId = store.addChildNodeInTabMode(parentNodeId);

      // Redistribute all children evenly
      store.redistributeChildNodes(parentNodeId);

      // Focus the new node after a brief delay
      setTimeout(() => {
        const newInput = document.querySelector(
          `[node-identifier="${newNodeId}"]`,
        ) as HTMLInputElement;
        if (newInput) {
          newInput.focus({ preventScroll: true });
          newInput.select();
        }
      }, 50);
    } else if (evt.key === "Escape") {
      // Stop Tab mode on ESC key
      const store = useStore.getState();
      if (store.tabModeActive) {
        evt.preventDefault();
        store.setTabModeActive(false);
        store.setTabModeParentId(null);
      }
    }
  };

  const handlePresentationOrder = (
    evt: React.ChangeEvent<HTMLInputElement>,
  ) => {
    console.log("handlePresentationOrder called, value:", evt.target.value);
    const inputValue = evt.target.value;
    // Only allow digits (0-9) and empty string
    const sanitized = inputValue.replace(/[^\d]/g, "");
    console.log("sanitized value:", sanitized);
    updatePresentationOrder(id, sanitized);
  };

  const handlePresentationOrderFocus = (
    evt: React.FocusEvent<HTMLInputElement>,
  ) => {
    console.log("Input focused, current value:", presentationOrder);
    setSelectedNodeId(id);
    // Select all text in the input field
    evt.target.select();
  };

  const handlePresentationOrderBlur = (
    evt: React.FocusEvent<HTMLInputElement>,
  ) => {
    console.log("Input blurred, value:", presentationOrder);
  };

  // Log whenever presentationOrder changes
  // useEffect(() => {
  //   console.log("presentationOrder changed to:", presentationOrder);
  // }, [presentationOrder]);

  const handlePriorityChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    cursorPositionRef.current = evt.target.selectionStart;
    const value = evt.target.value;
    const isValid =
      value === "" || /^[A-Za-z]\d*$/.test(value) || /^\d+$/.test(value);
    if (isValid) {
      updatePriority(id, value);
    }
  };

  const handleValueChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    cursorPositionRef.current = evt.target.selectionStart;
    const parsedValue = evt.target.value;
    updateValue(id, parsedValue);
  };

  // Combined selector for child node operations and rollup
  const { hideChildNodes, showChildNodes, doRollup, activateShowValues } =
    useStore(
      (state) => ({
        hideChildNodes: state.hideChildNodes,
        showChildNodes: state.showChildNodes,
        doRollup: state.doRollup,
        activateShowValues: state.showValues,
      }),
      shallow,
    );

  const [displayedNodeValue, setDisplayedNodeValue] = useState<string[]>([]);
  const [isNegativeValue, setIsNegativeValue] = useState(false);

  useEffect(() => {
    //console.log("showNodeValues", activateShowValues);
    const displayValues: string[] = [];
    if (doRollup) {
      const rollupValues = data.rollupValues || [];
      if (rollupValues) {
        rollupValues.forEach((rollup) => {
          if (rollup && rollup.title && rollup.value.length > 0) {
            displayValues.push(rollup.title + ": " + rollup.value);
          }
        });
      }
      setDisplayedNodeValue(displayValues);
    } else {
      const values = data.values || [];
      if (values) {
        values.forEach((value) => {
          if (value && value.title && value.value.length > 0) {
            displayValues.push(value.title + ": " + value.value);
          }
        });
      }
      setDisplayedNodeValue(displayValues);
    }
  }, [data.rollupValues, data.values, activateShowValues, doRollup]);

  // Single optimized selector for child visibility - avoids creating new arrays on every render
  const { hasHiddenChildren, hasVisibleChildren } = useStore((state) => {
    const childIds = state.childrenMap.get(id);
    if (!childIds || childIds.length === 0) {
      return { hasHiddenChildren: false, hasVisibleChildren: false };
    }

    let hidden = false;
    let visible = false;
    for (const childId of childIds) {
      const child = state.nodeMap.get(childId);
      if (child) {
        if (child.hidden) hidden = true;
        else visible = true;
        if (hidden && visible) break; // Early exit if we found both
      }
    }
    return { hasHiddenChildren: hidden, hasVisibleChildren: visible };
  }, shallow);

  const handleHideShowClick = (event: React.MouseEvent) => {
    const showAllLevels = event.shiftKey;
    if (hasHiddenChildren) {
      showChildNodes(id, showAllLevels);
    } else {
      hideChildNodes(id);
      setTimeout(() => {
        document.getSelection()?.removeAllRanges();
        inputRef.current?.blur();
        // divInputRef.current!.style.visibility = 'visible';
      }, 0);
    }
  };

  function handleDetailsClick(event: React.MouseEvent<Element>): void {
    event.stopPropagation();
    setNoteDetailsNodeId(showDetails ? null : id);
  }

  // Adjust note details position if it overflows viewport
  useEffect(() => {
    if (showDetails && noteDetailsRef.current) {
      const rect = noteDetailsRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustLeft = 0;
      let adjustTop = 0;

      // Check right overflow
      if (rect.right > viewportWidth) {
        adjustLeft = viewportWidth - rect.right - 10; // 10px padding
      }

      // Check left overflow
      if (rect.left < 0) {
        adjustLeft = -rect.left + 10; // 10px padding
      }

      // Check bottom overflow
      if (rect.bottom > viewportHeight) {
        adjustTop = viewportHeight - rect.bottom - 10; // 10px padding
      }

      // Check top overflow
      if (rect.top < 0) {
        adjustTop = -rect.top + 10; // 10px padding
      }

      // Apply adjustments
      if (adjustLeft !== 0) {
        noteDetailsRef.current.style.left = `${-10 + adjustLeft}px`;
      }
      if (adjustTop !== 0) {
        noteDetailsRef.current.style.marginTop = `${8 + adjustTop}px`;
      }
    }
  }, [showDetails]);

  const handleSaveWhy = (whyText: string) => {
    updateNodeData(id, { ...data, why: whyText });
  };

  return (
    <div
      key={id}
      className="focus-item-root"
      onDoubleClick={(e) => {
        e.stopPropagation();
        const div = inputRef.current;
        if (div) {
          div.focus();
          // Select all text in contenteditable
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(div);
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      }}
    >
      {(!isPresentationMode || presentWithShowHideButtons) && (
        <div>
          {hasVisibleChildren && !hasHiddenChildren && (
            <div className="focus-show-hide-area">
              <CiSquareMinus
                className="focus-show-hide-icon focus-show-hide-icon-minus"
                onClick={handleHideShowClick}
              />
            </div>
          )}
          {hasHiddenChildren && (
            <div className="focus-show-hide-area">
              <CiSquarePlus
                className="focus-show-hide-icon focus-show-hide-icon-plus"
                onClick={handleHideShowClick}
              />
            </div>
          )}
        </div>
      )}

      {/* Drill-down indicator - shows arrow based on context */}
      {(() => {
        if (isDrilldownFocus) {
          // In drill-down view - show up arrow
          return (
            <div className="drilldown-indicator focus-drilldown">
              <i
                className="pi pi-arrow-up drilldown-icon drilldown-up"
                title="Drill-down view active"
              />
            </div>
          );
        } else if (data.isDrilldownTab) {
          // Has active drill-down tab elsewhere - show down arrow
          return (
            <div className="drilldown-indicator focus-drilldown">
              <i
                className="pi pi-arrow-down drilldown-icon"
                title="Drill-down tab active"
              />
            </div>
          );
        }
        return null;
      })()}

      <div className="focus-presentation-order-area">
        {focusValue === "presentation" && (
          <input
            id="presentation-order-input-field"
            className="focus-presentation-order-input-field"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            spellCheck="false"
            value={presentationOrder}
            onChange={handlePresentationOrder}
            onFocus={handlePresentationOrderFocus}
            onBlur={handlePresentationOrderBlur}
            ref={presentationOrderInputRef}
          />
        )}
      </div>

      {focusValue === "priorities" ? (
        <div className="focus-priority-area">
          <input
            id="priority-input-field"
            className="focus-priority-input-field"
            spellCheck="false"
            value={data.priority}
            onChange={handlePriorityChange}
            onFocus={handleFocus}
            ref={priorityInputRef}
          />
        </div>
      ) : (
        data.priority &&
        (focusValue === "prioritiesView" || focusValue === "all") && (
          <div className="focus-priority-display">{data.priority}</div>
        )
      )}

      {focusValue === "rollup" ? (
        <div className="focus-rollup-area">
          <input
            id="rollup-input-field"
            className="focus-rollup-input-field"
            spellCheck="false"
            value={data.value}
            onChange={handleValueChange}
            onFocus={handleFocus}
            ref={valueInputRef}
          />
        </div>
      ) : (
        activateShowValues && (
          <div
            className={`focus-rollup-display ${
              isNegativeValue ? "negative" : ""
            }`}
          >
            <div className="focus-rollup-container">
              {displayedNodeValue.map((value, index) => (
                <div
                  key={index}
                  className={
                    doRollup ? "focus-rollup-value" : "focus-regular-value"
                  }
                >
                  {value}
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {useMemo(
        () => {
          return (
            <div
              style={{
                backgroundColor:
                  focusValue == "all" ? data.backgroundColor : "white",
              }}
              ref={dragRef}
              className={`dragHandle focus-dragHandle draghandle-focus ${
                isNodeSelected ? "selected" : ""
              } draghandle-color-standard`}
            >
              {/* Connection handles are nested inside the circle so edges
                  always attach to its actual visual center, regardless of the
                  dynamically computed diameter. */}
              <Handle
                type="target"
                position={Position.Top}
                style={{
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, 0%)",
                }}
                isConnectable={data.userRole === "viewer" ? false : isConnectable}
              />
              <Handle
                type="source"
                position={Position.Top}
                style={{
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, 0%)",
                }}
                isConnectable={data.userRole === "viewer" ? false : isConnectable}
              />
              {/* Cross-link handles, anchored to the circle's left/right edges */}
              <Handle
                type="source"
                position={Position.Right}
                id="cross-link"
                style={{ background: "red" }}
                isConnectable={data.userRole === "viewer" ? false : true}
              />
              <Handle
                type="target"
                position={Position.Left}
                id="cross-link"
                style={{ background: "blue" }}
                isConnectable={data.userRole === "viewer" ? false : true}
              />
              {/* Info/notes icon - anchored over the circle's left edge */}
              {data.notes && (
                <div className="focus-notes-indicator-left">
                  <BsFillInfoCircleFill
                    style={{ cursor: "pointer", fontSize: "1rem" }}
                    onClick={handleDetailsClick}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      const node = getNode(id);
                      if (node) setSelectedNode(node);
                      setSelectedNodeId(id);
                      setCurrentNodeLabel(data.label);
                      setCurrentLinks(data.links || []);
                      setCurrentNotes(data.notes);
                      setShowNotesModal(true);
                    }}
                  />
                </div>
              )}
              {/* Arc satellite badges */}
              {renderFocusSatelliteBadges()}
            </div>
          );
        },
        [
          data.backgroundColor,
          data.notes,
          data.userRole,
          focusValue,
          isNodeSelected,
          isConnectable,
          id,
          satelliteBadges,
        ],
      )}

      <div ref={wrapperRef} className="focus-inputWrapper">
        <div className="inputArea-focus">
          {!isPresentationMode && data.icon && data.icon.length > 0 ? (
            // Match NodeItem top-center icon placement
            /^https?:\/\//.test(data.icon) ||
            data.icon.endsWith(".png") ||
            data.icon.endsWith(".jpg") ||
            data.icon.endsWith(".jpeg") ||
            data.icon.startsWith("data:image/") ? (
              <>
                <OverlayPanel ref={op}>
                  <img
                    src={data.icon}
                    alt="node-image"
                    className="node-overlay-image"
                  />
                </OverlayPanel>
                <div className="custom-node-icon node-top-icon dragHandle">
                  <img
                    className="node-top-icon-img"
                    src={data.icon}
                    alt="node-icon"
                    onClick={(e) => op.current?.toggle(e)}
                  />
                </div>
              </>
            ) : (
              <div className="custom-node-icon node-top-icon node-top-icon-emoji-container dragHandle">
                <span className="node-top-icon-emoji">{data.icon}</span>
              </div>
            )
          ) : null}
          <div
            id="focus-input-field"
            className="focus-input"
            node-identifier={id}
            contentEditable={data.userRole === "viewer" ? "false" : "true"}
            suppressContentEditableWarning
            spellCheck="false"
            onInput={handleChange}
            onPaste={(evt) => {
              if (data.userRole === "viewer") return;
              evt.preventDefault();
              const text = evt.clipboardData.getData("text/plain");
              document.execCommand("insertText", false, text);
            }}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            onKeyDown={handleKeyDown}
            onFocus={data.userRole === "viewer" ? undefined : handleFocus}
            onBlur={data.userRole === "viewer" ? undefined : handleBlur}
            onClick={() => setSelectedNodeId(id)}
            ref={inputRef}
          />
          {/* Flag icon - positioned above node */}
          {/* {data.flagged &&
            data.flagged !== "none" &&
            (focusValue === "marker" || focusValue === "all") && (
              <div className="focus-icons-row">
                <IoFlag
                  className="focus-flag-icon"
                  style={{
                    cursor: "pointer",
                    fontSize: "0.8rem",
                    color:
                      data.flagged === "green"
                        ? "#8a9a5b"
                        : data.flagged === "yellow"
                        ? "#d2b773"
                        : data.flagged === "red"
                        ? "#b76e79"
                        : "#126656",
                  }}
                />
              </div>
            )} */}
          {/* Marker icon (matches NodeItem: size + location) */}
          {(data.marker || (data.complete && !data.marker)) &&
            !isPresentationMode && (
              <div
                className={`node-marker-icon ${
                  data.marker === "alert"
                    ? "is-alert"
                    : data.marker === "question"
                      ? "is-question"
                      : data.marker === "strength"
                        ? "is-strength"
                        : data.marker === "weakness"
                          ? "is-weakness"
                          : data.marker === "opportunity"
                            ? "is-opportunity"
                            : data.marker === "threat"
                              ? "is-threat"
                      : "is-complete"
                }`}
                onClick={() => {
                  setSelectedNodeId(id);
                  const node = getNode(id);
                  if (node) setSelectedNode(node);
                }}
              >
                {data.marker === "question" ? (
                  "?"
                ) : data.marker === "alert" ? (
                  "!"
                ) : data.marker === "strength" ? (
                  <i className="pi pi-thumbs-up" />
                ) : data.marker === "weakness" ? (
                  <i className="pi pi-thumbs-down" />
                ) : data.marker === "opportunity" ? (
                  <i className="pi pi-bolt" />
                ) : data.marker === "threat" ? (
                  <i className="pi pi-exclamation-triangle" />
                ) : (
                  <LuCheck size={14} />
                )}
              </div>
            )}
        </div>

        {/* Properties display below node */}
        {activateShowValues &&
          (() => {
            const visibleProps =
              data.customProperties?.filter((prop) => prop.visible) || [];
            const rollupProps =
              doRollup && data.rollupValues ? data.rollupValues : [];
            const hasContent =
              visibleProps.length > 0 || rollupProps.length > 0;

            if (!hasContent) return null;

            return (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  marginTop: "4px",
                  padding: "4px 8px",
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  fontSize: "0.75rem",
                  whiteSpace: "nowrap",
                  zIndex: 1,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                {/* Show rollup values when enabled */}
                {doRollup &&
                  rollupProps.map((rollup, idx) => (
                    <div
                      key={`rollup-${idx}`}
                      style={{
                        display: "flex",
                        gap: "4px",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontWeight: 600, color: "#666" }}>
                        {rollup.title}:
                      </span>
                      <span style={{ color: "#333" }}>{rollup.value}</span>
                    </div>
                  ))}
                {/* Show custom properties when not in rollup mode or in addition */}
                {!doRollup &&
                  visibleProps.map((prop, idx) => {
                    let displayValue: string;

                    if (prop.type === "calculated") {
                      const currency = useStore.getState().currency || "EUR";
                      const calculated = getCalculatedValue(
                        prop,
                        data.customProperties || [],
                        currency,
                      );
                      displayValue = calculated ? calculated.value : "0";
                    } else if (prop.type === "date" && prop.value) {
                      displayValue = new Date(prop.value).toLocaleDateString();
                    } else if (prop.type === "boolean") {
                      displayValue = prop.value ? "✓" : "✗";
                    } else {
                      displayValue = String(prop.value || "");
                    }

                    return (
                      <div
                        key={`prop-${idx}`}
                        style={{
                          display: "flex",
                          gap: "4px",
                          alignItems: "center",
                        }}
                      >
                        <span style={{ fontWeight: 600, color: "#666" }}>
                          {prop.name}:
                        </span>
                        <span style={{ color: "#333" }}>{displayValue}</span>
                      </div>
                    );
                  })}
              </div>
            );
          })()}
      </div>

      {/* Why tag for root nodes */}
      {isRootNode && showWhyTag && (
        <div
          style={{
            position: "absolute",
            bottom: "-14px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10,
            cursor: "pointer",
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            setShowWhyDialog(true);
          }}
        >
          <Tag
            value={data.why ? "why✓" : "why?"}
            style={{
              fontSize: "0.375rem",
              padding: "0.125rem 0.25rem",
              backgroundColor: data.why ? "rgb(18, 102, 86)" : "#4C566B",
              color: "#ffffff",
              border: "none",
            }}
          />
        </div>
      )}

      <div
        ref={noteDetailsRef}
        className={`focus-node-details-area${showDetails ? " visible" : ""}`}
      >
        <div className="focus-node-details-label">
          <ReactMarkdown
            components={{
              h1: ({ node, ...props }) => (
                <h1
                  style={{
                    fontSize: "2em",
                    marginTop: "0em",
                    marginBottom: "0em",
                    paddingTop: "0em",
                    paddingBottom: "0em",
                  }}
                  {...props}
                />
              ),
              h2: ({ node, ...props }) => (
                <h2
                  style={{
                    fontSize: "1.5em",
                    marginTop: "0em",
                    marginBottom: "0em",
                    paddingTop: "0em",
                    paddingBottom: "0em",
                  }}
                  {...props}
                />
              ),
              h3: ({ node, ...props }) => (
                <h3
                  style={{
                    fontSize: "1.2em",
                    marginTop: "0em",
                    marginBottom: "0em",
                    paddingTop: "0em",
                    paddingBottom: "0em",
                  }}
                  {...props}
                />
              ),
              h4: ({ node, ...props }) => (
                <h4
                  style={{
                    fontSize: "1.1em",
                    marginTop: "0em",
                    marginBottom: "0em",
                    paddingTop: "0em",
                    paddingBottom: "0em",
                  }}
                  {...props}
                />
              ),
              p: ({ node, ...props }) => (
                <p
                  style={{
                    fontSize: "1em",
                    marginTop: "0em",
                    marginBottom: "0.3em",
                    paddingTop: "0em",
                    paddingBottom: "0em",
                  }}
                  {...props}
                />
              ),
              ul: ({ node, ...props }) => (
                <ul
                  style={{
                    fontSize: "1em",
                    margin: "0em",
                    paddingInlineStart: "1em", // reduced from 1.2em to 1em
                    listStylePosition: "outside",
                  }}
                  {...props}
                />
              ),
              ol: ({ node, ...props }) => (
                <ol
                  style={{
                    fontSize: "1em",
                    margin: "0em",
                    paddingInlineStart: "1em", // reduced from 1.2em to 1em
                    listStylePosition: "outside",
                  }}
                  {...props}
                />
              ),
              li: ({ node, ...props }) => (
                <li
                  style={{
                    fontSize: "1em",
                    margin: "0em",
                    paddingInlineStart: "0em", // set to 0 to minimize bullet gap
                  }}
                  {...props}
                />
              ),
              input: ({ node, ...props }) =>
                props.type === "checkbox" ? (
                  <input
                    style={{
                      fontSize: "1em",
                      margin: "0em",
                      paddingInlineStart: "0em",
                    }}
                    {...props}
                  />
                ) : (
                  <input {...props} />
                ),
              table: () => null,
              thead: () => null,
              tbody: () => null,
              tr: () => null,
              th: () => null,
              td: () => null,
            }}
          >
            {convertHtmlToMarkdown(data.notes)}
          </ReactMarkdown>
        </div>
      </div>

      {/* Why Dialog */}
      <WhyDialog
        visible={showWhyDialog}
        initialWhy={data.why || ""}
        onSave={handleSaveWhy}
        onHide={() => setShowWhyDialog(false)}
      />
    </div>
  );
}

// Custom comparison function that ignores position changes to prevent re-renders during drag
export default memo(FocusItem, (prevProps, nextProps) => {
  // If only position changed, don't re-render
  return (
    prevProps.id === nextProps.id &&
    prevProps.data === nextProps.data &&
    prevProps.selected === nextProps.selected &&
    prevProps.isConnectable === nextProps.isConnectable
  );
});
